/**
 * Utility for handling API errors and providing user-friendly messages.
 */

import { ErrorResponse } from './errorUtils';

// Re-export ErrorResponse for backward compatibility
export type { ErrorResponse } from './errorUtils';

// Legacy ApiError type - use ErrorResponse instead
export type ApiError = ErrorResponse;

export const getErrorMessage = (error: any): string => {
  // If it's already a string, return it
  if (typeof error === 'string') {
    return error;
  }

  // If it's an API error response
  if (error?.response?.data) {
    const apiError: ErrorResponse = error.response.data;

    // Handle specific error types (from error field)
    if (apiError.error) {
      switch (apiError.error) {
        case 'ValidationError':
          if (apiError.fieldErrors) {
            const fieldErrors = Object.entries(apiError.fieldErrors)
              .map(([field, message]) => `${field}: ${message}`)
              .join(', ');
            return `Please fix the following errors: ${fieldErrors}`;
          }
          return apiError.message || 'Please check your input and try again.';

        case 'ResourceNotFound':
          return apiError.message || 'The requested resource was not found.';

        case 'AccessDenied':
          return apiError.message || 'You don\'t have permission to perform this action.';

        case 'Unauthorized':
          return apiError.message || 'You are not authorized to perform this action. Please log in again.';

        case 'DuplicateResource':
        case 'DuplicateUsername':
        case 'DuplicateEmail':
          return apiError.message || 'This resource already exists.';
      }
    }

    // Handle specific error codes (legacy support)
    if (apiError.errorCode) {
      switch (apiError.errorCode) {
        case 'INVOICE_NUMBER_DUPLICATE':
          return apiError.message || 'This invoice number already exists. Please choose a different number.';

        case 'COMPANY_NOT_FOUND':
          return 'The selected company was not found. Please refresh the page and try again.';

        case 'CLIENT_NOT_FOUND':
          return 'The selected client was not found. Please refresh the page and try again.';

        case 'INVOICE_NOT_FOUND':
          return 'The invoice was not found. It may have been deleted or you don\'t have permission to access it.';

        case 'FILE_SIZE_EXCEEDED':
          return 'The uploaded file is too large. Please choose a smaller file (max 10MB).';
      }
    }

    // Return the message from the response
    return apiError.message || apiError.error || 'An error occurred. Please try again.';
  }

  // Handle network errors
  if (error?.message) {
    if (error.message.includes('Network Error')) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }
    if (error.message.includes('timeout')) {
      return 'Request timed out. Please try again.';
    }
    return error.message;
  }

  // Handle HTTP status codes
  if (error?.response?.status) {
    switch (error.response.status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'You are not authorized to perform this action. Please log in again.';
      case 403:
        return 'You don\'t have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 413:
        return 'The uploaded file is too large. Please choose a smaller file.';
      case 500:
        return 'Server error. Please try again later.';
      default:
        return `An error occurred (${error.response.status}). Please try again.`;
    }
  }

  // Default error message
  return 'An unexpected error occurred. Please try again.';
};

export const getFieldErrors = (error: any): Record<string, string> => {
  if (error?.response?.data?.fieldErrors) {
    return error.response.data.fieldErrors;
  }
  return {};
};

export const hasFieldError = (error: any, fieldName: string): boolean => {
  const fieldErrors = getFieldErrors(error);
  return fieldName in fieldErrors;
};

export const getFieldErrorMessage = (error: any, fieldName: string): string => {
  const fieldErrors = getFieldErrors(error);
  return fieldErrors[fieldName] || '';
};

/**
 * Get the trace ID from an error response for debugging/support purposes.
 * 
 * @param error - The error object from catch block
 * @returns The trace ID or undefined
 */
export const getTraceId = (error: any): string | undefined => {
  const errorResponse: ErrorResponse | undefined = error?.response?.data;
  return errorResponse?.traceId;
};

